/**
 * Cart drawer: add-to-cart feedback (skeleton + line update states)
 *
 * Standalone add-on for Horizon. Does not touch any base theme JS.
 *
 * How it works
 * - Listens to the same `shopify:cart:lines-update` event the theme dispatches on add-to-cart.
 * - New line: a placeholder row (from <template id="CartAddSkeleton">, rendered by the
 *   cart-add-skeleton snippet) is inserted at the top of the drawer, where Shopify puts new
 *   lines. Horizon's morph matches rows by id, so the skeleton has no counterpart and is removed
 *   in the same pass the real row is inserted. While a swap is pending, the theme's "grow from 0"
 *   row animation is replaced by a fade, because the space is already reserved.
 * - Empty cart: the skeleton is shown inside the empty drawer and the drawer opens right away,
 *   like it does for a filled cart. Horizon's empty → filled view transition would otherwise run
 *   while the drawer is still sliding in (it animates a frozen snapshot of the drawer contents
 *   separately from the drawer itself); it is replaced by a short fade.
 * - Existing line: if the add can only merge into one existing line (same variant, no line item
 *   properties), that line shows a loading state (dimmed + shimmer sweep) instead of a skeleton.
 * - Unknown: Shopify only merges an add into an existing line when variant AND line item
 *   properties match. Sets / personalised products share a variant but may differ in their
 *   properties, so they get a skeleton. If the add merged after all, the skeleton collapses.
 * - After the response, lines are compared by their line key (data-key) before and after:
 *   every line whose quantity or price changed gets a short highlight, new lines replace the
 *   skeleton. Nothing is guessed from the variant id and no line is removed or re-added here.
 * - On error (or if the request never settles) everything is reverted.
 */

import { viewTransition } from '@theme/utilities';

const EVENT_NAME = 'shopify:cart:lines-update';
const TEMPLATE_ID = 'CartAddSkeleton';
const DRAWER_ITEMS_SELECTOR = 'cart-items-component[data-drawer]';
const ROW_SELECTOR = 'tr.cart-items__table-row[data-key]';
const SWAP_ATTRIBUTE = 'data-cart-skeleton-swap';
const FILL_ATTRIBUTE = 'data-cart-skeleton-fill';
const EMPTY_ATTRIBUTE = 'data-cart-skeleton-empty';
const PENDING_ATTRIBUTE = 'data-cart-pending';
const UPDATED_CLASS = 'cart-row-updated';
const MAX_SKELETONS = 3;
const RENDER_TIMEOUT = 5000; // stop waiting for the drawer re-render after this
const SWAP_RELEASE_DELAY = 800; // keep the fade override until the reveal animation is done
const FILL_RELEASE_DELAY = 400; // keep the fill fade until it is done
const UPDATED_DURATION = 1600; // matches the highlight animation in the snippet
const FAILSAFE_TIMEOUT = 10000; // clean up if the add request never settles

let skeletonUid = 0;
let pendingSwaps = 0;
let pendingFills = 0;

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const prefersReducedMotion = () => reducedMotionQuery.matches;
const toVariantId = (id) => String(id ?? '').split('/').pop();

document.addEventListener(EVENT_NAME, (event) => {
  if (event.action !== 'add' || !event.promise) return;

  const template = document.getElementById(TEMPLATE_ID);
  const cartItems = document.querySelector(DRAWER_ITEMS_SELECTOR);
  if (!(template instanceof HTMLTemplateElement) || !(cartItems instanceof HTMLElement)) return;

  const tbody = cartItems.querySelector('.cart-items__table tbody');
  const emptyContent = tbody
    ? null
    : cartItems.querySelector('[data-cart-drawer-empty]')?.closest('.cart-drawer__content') ?? null;
  if (!tbody && !emptyContent) return;

  const dialog = cartItems.closest('dialog');
  const drawerAlreadyOpen =
    dialog?.open && !dialog.matches('.theme-drawer__dialog--opening, .theme-drawer__dialog--opening-inline-start');

  const before = readLines(cartItems);
  const plainAdd = !hasLineItemProperties(event.target);
  const lines = Array.isArray(event.lines) && event.lines.length ? event.lines : [{}];

  /** @type {HTMLTableRowElement[]} */
  const pendingRows = [];
  let newLineCount = 0;

  for (const line of lines) {
    const rows = tbody ? findRowsByVariant(tbody, toVariantId(line.merchandiseId)) : [];
    // Certain merge: a plain add and exactly one top-level line of that variant.
    // Anything else (sets, personalised items, several lines of the variant) may become a new line.
    if (plainAdd && rows.length === 1 && rows[0]) {
      if (!pendingRows.includes(rows[0])) pendingRows.push(rows[0]);
    } else {
      newLineCount++;
    }
  }

  for (const row of pendingRows) setPending(row);

  // Match the drawer's image ratio setting (square / portrait) from an existing row.
  const ratio = tbody?.querySelector('.cart-items__media-container')?.style.getPropertyValue('--ratio');

  /** @type {HTMLTableRowElement[]} */
  const skeletons = [];
  for (let i = 0; i < Math.min(newLineCount, MAX_SKELETONS); i++) {
    skeletons.push(createSkeleton(template, ratio));
  }

  /** @type {HTMLElement | null} */
  let emptyList = null;
  let openedDrawer = false;

  if (skeletons.length) {
    if (tbody) {
      tbody.prepend(...skeletons);
    } else if (emptyContent) {
      emptyList = createEmptyList(skeletons);
      emptyContent.prepend(emptyList);
      emptyContent.setAttribute(EMPTY_ATTRIBUTE, '');
      acquireFill();
      openedDrawer = openDrawer(event, cartItems);
    }
    acquireSwap();

    // Drawer closed or still sliding in → show instantly; the slide is the motion.
    if (drawerAlreadyOpen && !prefersReducedMotion()) {
      for (const row of skeletons) {
        row.classList.add('adding');
        row.addEventListener('animationend', (e) => e.target === row && row.classList.remove('adding'), {
          once: true,
        });
      }
    }
  }

  const focusRow = skeletons[0] ?? pendingRows[0];
  if (focusRow) requestAnimationFrame(() => scrollIntoView(focusRow));

  let settled = false;

  const onSuccess = async () => {
    if (settled) return;
    settled = true;

    await observeRender({
      root: dialog ?? document.body,
      cartItems,
      before,
      skeletons,
      // Runs right after the morph, before the browser paints it.
      onRendered: () => {
        const { newRows, updatedRows } = diffLines(cartItems, before);

        for (const row of pendingRows) clearPending(row);
        settleSkeletons(skeletons, newRows, cartItems);
        for (const row of updatedRows) highlightRow(row);

        // A merged add that showed a skeleton: point at the line that changed instead.
        const target = newRows[0] ?? updatedRows[0];
        if (target && target !== focusRow) requestAnimationFrame(() => scrollIntoView(target));
      },
    });

    if (skeletons.length) setTimeout(releaseSwap, SWAP_RELEASE_DELAY);
    if (emptyList) releaseFillAfterTransition();
  };

  const onFailure = () => {
    if (settled) return;
    settled = true;

    for (const row of pendingRows) clearPending(row);
    skeletons.forEach(collapseRow);
    if (skeletons.length) releaseSwap();

    if (emptyList) {
      emptyList.remove();
      emptyContent?.removeAttribute(EMPTY_ATTRIBUTE);
      releaseFill();
      // Opened only for the skeleton: close again so the product form's error is visible.
      if (openedDrawer && cartItems.querySelector('[data-cart-drawer-empty]')) {
        /** @type {any} */ (cartItems.closest('theme-drawer'))?.close?.();
      }
    }
  };

  const failsafe = setTimeout(onFailure, FAILSAFE_TIMEOUT);
  event.promise.finally(() => clearTimeout(failsafe)).catch(() => {});
  event.promise.then((result) => (result?.detail?.didError ? onFailure() : onSuccess()), onFailure);
});

/**
 * Whether the add carries line item properties or a selling plan. Shopify only merges an add into
 * an existing line when these match, which can't be known here. Unknown sources count as "yes".
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
function hasLineItemProperties(target) {
  if (!(target instanceof Element)) return true;

  const form =
    target.querySelector('form[data-type="add-to-cart-form"]') ?? (target instanceof HTMLFormElement ? target : null);
  if (!(form instanceof HTMLFormElement)) return true;

  // FormData includes inputs outside the form that point to it via the `form` attribute.
  for (const [name, value] of new FormData(form)) {
    if (typeof value !== 'string' || !value.trim()) continue;
    if (name.startsWith('properties[') || name === 'selling_plan') return true;
  }

  return false;
}

/**
 * Top-level lines (no nested bundle components) of a variant.
 * @param {HTMLElement} tbody
 * @param {string} variantId
 * @returns {HTMLTableRowElement[]}
 */
function findRowsByVariant(tbody, variantId) {
  if (!variantId) return [];

  const rows = new Set();
  for (const element of tbody.querySelectorAll(`[data-variant-id="${CSS.escape(variantId)}"]`)) {
    const row = element.closest(ROW_SELECTOR);
    if (row instanceof HTMLTableRowElement && !row.dataset.parentKey) rows.add(row);
  }
  return [...rows];
}

/**
 * Line key → quantity and line price, used to tell new and changed lines apart after the response.
 * @param {HTMLElement} cartItems
 * @returns {Map<string, string>}
 */
function readLines(cartItems) {
  const lines = new Map();
  for (const row of cartItems.querySelectorAll(ROW_SELECTOR)) {
    if (!(row instanceof HTMLElement) || !row.dataset.key) continue;
    const quantity = row.querySelector('input[name="updates[]"]')?.getAttribute('value') ?? '';
    const price = row.querySelector('.cart-items__price text-component')?.getAttribute('value') ?? '';
    lines.set(row.dataset.key, `${quantity}|${price}`);
  }
  return lines;
}

/**
 * @param {HTMLElement} cartItems
 * @param {Map<string, string>} before
 */
function diffLines(cartItems, before) {
  const after = readLines(cartItems);
  /** @type {HTMLTableRowElement[]} */
  const newRows = [];
  /** @type {HTMLTableRowElement[]} */
  const updatedRows = [];

  for (const row of cartItems.querySelectorAll(ROW_SELECTOR)) {
    if (!(row instanceof HTMLTableRowElement) || !row.dataset.key) continue;
    const key = row.dataset.key;
    if (!before.has(key)) newRows.push(row);
    else if (before.get(key) !== after.get(key)) updatedRows.push(row);
  }

  return { newRows, updatedRows };
}

/**
 * Resolves once the drawer shows the response: a skeleton was removed by the morph or a line
 * changed. `onRendered` runs synchronously in that moment (a MutationObserver callback runs
 * after the morph and before the next paint), so follow-up DOM changes never flash.
 *
 * @param {{
 *   root: Node,
 *   cartItems: HTMLElement,
 *   before: Map<string, string>,
 *   skeletons: HTMLElement[],
 *   onRendered: () => void
 * }} options
 * @returns {Promise<void>}
 */
function observeRender({ root, cartItems, before, skeletons, onRendered }) {
  return new Promise((resolve) => {
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(timer);
      onRendered();
      resolve();
    };

    const check = () => {
      if (skeletons.some((row) => !row.isConnected) || linesChanged(before, readLines(cartItems))) finish();
    };

    const observer = new MutationObserver(check);
    const timer = setTimeout(finish, RENDER_TIMEOUT);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'data-key'],
    });
    check();
  });
}

/**
 * @param {Map<string, string>} before
 * @param {Map<string, string>} after
 */
function linesChanged(before, after) {
  if (before.size !== after.size) return true;
  for (const [key, value] of after) {
    if (before.get(key) !== value) return true;
  }
  return false;
}

/**
 * The morph removes every skeleton. If fewer new lines arrived than skeletons were shown
 * (the add merged into an existing line), put the surplus back right after the new lines
 * and collapse it, so the list closes the gap smoothly instead of jumping.
 *
 * @param {HTMLTableRowElement[]} skeletons
 * @param {HTMLTableRowElement[]} newRows
 * @param {HTMLElement} cartItems
 */
function settleSkeletons(skeletons, newRows, cartItems) {
  let surplus = Math.max(0, skeletons.length - newRows.length);
  const tbody = cartItems.querySelector('.cart-items__table tbody');
  const lastNewRow = newRows[newRows.length - 1];

  for (const skeleton of skeletons) {
    if (skeleton.isConnected) {
      collapseRow(skeleton);
    } else if (surplus > 0 && tbody && !prefersReducedMotion()) {
      surplus--;
      tbody.insertBefore(skeleton, lastNewRow ? lastNewRow.nextElementSibling : tbody.firstElementChild);
      collapseRow(skeleton);
    }
  }
}

/**
 * @param {HTMLTemplateElement} template
 * @param {string} [ratio]
 * @returns {HTMLTableRowElement}
 */
function createSkeleton(template, ratio) {
  const row = /** @type {HTMLTableRowElement} */ (document.importNode(template.content, true).firstElementChild);
  row.id = `CartItemSkeleton-${++skeletonUid}`;
  if (ratio) row.querySelector('.cart-items__media-container')?.style.setProperty('--ratio', ratio);
  return row;
}

/**
 * A minimal copy of the cart list markup, so the skeleton in the empty drawer sits where the
 * first line will appear and uses the theme's own row layout.
 * @param {HTMLTableRowElement[]} skeletons
 * @returns {HTMLElement}
 */
function createEmptyList(skeletons) {
  const wrapper = document.createElement('div');
  wrapper.className = 'cart-drawer__items cart-skeleton-list';
  wrapper.setAttribute('aria-hidden', 'true');

  const list = document.createElement('div');
  list.className = 'cart-items';

  const table = document.createElement('table');
  table.className = 'cart-items__table';
  table.setAttribute('role', 'presentation');

  const tbody = document.createElement('tbody');
  tbody.append(...skeletons);
  table.append(tbody);
  list.append(table);
  wrapper.append(list);
  return wrapper;
}

/**
 * Opens the cart drawer right away (empty cart), the way Horizon already does for a filled cart.
 * Mirrors the conditions of cart-drawer.js: auto-open enabled, not added from a modal (quick add
 * restores its focus first, the drawer opens when it closes).
 * @param {Event} event
 * @param {HTMLElement} cartItems
 * @returns {boolean} Whether the drawer was opened here
 */
function openDrawer(event, cartItems) {
  const drawer = /** @type {any} */ (cartItems.closest('theme-drawer'));
  const component = cartItems.closest('cart-drawer-component');

  if (!drawer || typeof drawer.open !== 'function' || drawer.isOpen) return false;
  if (!component?.hasAttribute('auto-open')) return false;
  if (event.target instanceof Element && event.target.closest('dialog:modal')) return false;

  drawer.open();
  return true;
}

/** @param {HTMLTableRowElement} row */
function setPending(row) {
  row.setAttribute(PENDING_ATTRIBUTE, '');
  row.setAttribute('aria-busy', 'true');
  row.querySelectorAll('text-component').forEach((el) => el.setAttribute('shimmer', ''));
}

/** @param {HTMLTableRowElement} row */
function clearPending(row) {
  row.removeAttribute(PENDING_ATTRIBUTE);
  row.removeAttribute('aria-busy');
  row.querySelectorAll('text-component[shimmer]').forEach((el) => el.removeAttribute('shimmer'));
}

/**
 * Short highlight on a line that changed (quantity / price).
 * @param {HTMLTableRowElement} row
 */
function highlightRow(row) {
  // The highlight ends above the row's bottom spacing, so it only covers the line itself.
  row.style.setProperty('--cart-row-spacing-end', getComputedStyle(row).paddingBottom);
  row.classList.remove(UPDATED_CLASS);
  row.classList.add(UPDATED_CLASS);
  setTimeout(() => {
    row.classList.remove(UPDATED_CLASS);
    row.style.removeProperty('--cart-row-spacing-end');
  }, UPDATED_DURATION);
}

/** @param {Element} element */
function scrollIntoView(element) {
  if (!element.isConnected) return;
  element.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}

/** Removes a row using the theme's own `removing` animation. */
function collapseRow(row) {
  if (!row.isConnected) return;
  if (prefersReducedMotion()) return row.remove();

  row.style.setProperty('--row-height', `${row.clientHeight}px`);
  row.classList.remove('adding');
  row.classList.add('removing');
  row.addEventListener('animationend', (e) => e.target === row && row.remove());
  setTimeout(() => row.remove(), 1500);
}

function acquireSwap() {
  pendingSwaps++;
  document.documentElement.setAttribute(SWAP_ATTRIBUTE, '');
}

function releaseSwap() {
  pendingSwaps = Math.max(0, pendingSwaps - 1);
  if (!pendingSwaps) document.documentElement.removeAttribute(SWAP_ATTRIBUTE);
}

function acquireFill() {
  pendingFills++;
  document.documentElement.setAttribute(FILL_ATTRIBUTE, '');
}

function releaseFill() {
  pendingFills = Math.max(0, pendingFills - 1);
  if (!pendingFills) document.documentElement.removeAttribute(FILL_ATTRIBUTE);
}

/**
 * The fill attribute neutralises Horizon's empty → filled view transition. Keep it until that
 * transition (if any) is over: removing it earlier would start the transition's animations.
 */
function releaseFillAfterTransition() {
  requestAnimationFrame(() => {
    Promise.resolve(viewTransition.current)
      .catch(() => {})
      .then(() => setTimeout(releaseFill, FILL_RELEASE_DELAY));
  });
}